/**
 * Helpers for company category / subCategory stored as number | number[].
 * Prefer hasSubCategory / hasAnySubCategory / matchesId over picking a single id.
 */

export type IdValue = number | number[] | null | undefined;

const isValidId = (v: unknown): v is number =>
  typeof v === 'number' && !Number.isNaN(v);

/** Normalize to a clean number[] (empty if missing/invalid). */
export const toIdArray = (value: IdValue): number[] => {
  if (value == null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.filter(isValidId);
  }
  if (isValidId(value)) {
    return [value];
  }

  return [];
};

const toConfigIdArray = (value: IdValue): number[] => toIdArray(value);

/**
 * Match company category/subcategory against config value.
 * Scalar: strict equality. Arrays: any overlap (OR).
 */
export const matchesId = (
  companyValue: IdValue,
  configValue: IdValue
): boolean => {
  if (configValue == null || configValue === undefined) {
    return true;
  }

  const companyIds = toIdArray(companyValue);
  const configIds = toConfigIdArray(configValue);

  if (configIds.length === 0) {
    return true;
  }
  if (companyIds.length === 0) {
    return false;
  }

  return companyIds.some((id) => configIds.includes(id));
};

/** Same semantics as legacy config.includes(company) / company === config. */
export const matchesSubCategory = (
  companyValue: IdValue,
  configValue: IdValue
): boolean => matchesId(companyValue, configValue);

export const hasSubCategory = (companyValue: IdValue, id: number): boolean =>
  toIdArray(companyValue).includes(id);

export const hasAnySubCategory = (
  companyValue: IdValue,
  ids: number[]
): boolean => {
  const companyIds = toIdArray(companyValue);

  return companyIds.some((id) => ids.includes(id));
};

/** Path segment for APIs: one id as number, multiple as comma-separated. */
export const subCategoryPathSegment = (
  value: IdValue
): string | number | undefined => {
  const ids = toIdArray(value);
  if (ids.length === 0) {
    return 0;
  }
  if (ids.length === 1) {
    return ids[0];
  }

  return ids.join(',');
};

/** Normalize store fields to number[] after API load. */
export const normalizeIdField = (value: unknown): number[] => {
  if (typeof value === 'number') {
    return isValidId(value) ? [value] : [];
  }
  if (Array.isArray(value)) {
    return value.filter(isValidId);
  }

  return [];
};
